import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/* Project */
import { selectState } from 'utils/reduxActions';
import connectScreen from 'utils/connectScreen';
import getQueryParams from 'utils/services/getQueryParams';
import { appActions } from 'commons/reducer';
import { useLocation } from 'react-router-dom';
import Components from './components';
import { lotTraceabilityActions } from './reducer';
import saga from './saga';
import styles from './styles';
import ProcessTraceabilityChart from './components/ProcessTraceabilityChart';
import { shouldUseCallOffInTraceabilityGraph } from '../../utils/tenantFeatures';

function LotTraceability(props) {
  const [dataSet, setDataSet] = useState([]);
  const [dataLabels, setDataLabels] = useState([]);
  const location = useLocation();
  const { t } = useTranslation();
  const useCallOffReference = shouldUseCallOffInTraceabilityGraph();

  const {
    actions,
    lotTraceability: { loading, traceabilityReport, traceabilityGraph, controls },
    classes,
    history,
    queueProcess,
    queueData,
    loadFromNoti,
    socketId,
  } = props;

  const buildTraceabilityParams = () => {
    const rawParams = getQueryParams();
    const searchParams = new URLSearchParams(location.search || '');

    const normalizeIds = (values = []) =>
      values
        .flatMap((value) => `${value}`.split(','))
        .map((value) => value.trim())
        .filter((value) => /^\d+$/.test(value));

    // Canonicalize lotIds from multiple possible formats in URL.
    let lotIds = normalizeIds(
      [
        ...(searchParams.getAll('lotIds') || []),
        ...(searchParams.getAll('lotIds[]') || []),
      ],
    );

    if (!lotIds.length && rawParams.lotIds !== undefined) {
      const parsed = Array.isArray(rawParams.lotIds)
        ? rawParams.lotIds
        : [rawParams.lotIds];
      lotIds = normalizeIds(parsed);
    }

    const lotNumber = searchParams.get('lotNumber') || rawParams.lotNumber || null;

    return {
      ...rawParams,
      lotIds,
      lotNumber,
    };
  };

  useEffect(() => {
    const params = buildTraceabilityParams();
    actions.changeControls(params);

    if (!loadFromNoti) {
      actions.getStateFromApi({ ...params, socketId });
    }

    return () => {
      actions.loadFromNotification({ fromNoti: false });
      actions.resetQueueNotificationStatus();
    };
  }, [loadFromNoti]);

  const refreshData = () => {
    actions.getStateFromApi({
      ...controls,
      lotIds: Array.isArray(controls?.lotIds) ? controls.lotIds : [],
      redo: controls?.lotNumber ? undefined : true,
      socketId,
    });
  };

  const createDataSetsFromGraph = () => {
    const nodes = Array.isArray(traceabilityGraph?.nodes) ? traceabilityGraph.nodes : [];
    const edges = Array.isArray(traceabilityGraph?.edges) ? traceabilityGraph.edges : [];

    if (!nodes.length) {
      setDataLabels([]);
      setDataSet([]);
      return;
    }

    const nodesByLevel = nodes.reduce((acc, node) => {
      if (!acc[node.level]) acc[node.level] = [];
      acc[node.level].push(node);
      return acc;
    }, {});

    const orderedLevels = Object.keys(nodesByLevel)
      .map((level) => Number(level))
      .sort((a, b) => a - b);

    const orderedSteps = orderedLevels.map((level) => {
      const stepNodes = (nodesByLevel[level] || []).sort((a, b) => a.processId - b.processId);
      return stepNodes;
    });

    const outgoingByProcess = new Map();
    const incomingByProcess = new Map();

    edges.forEach((edge) => {
      if (!outgoingByProcess.has(edge.fromProcessId)) outgoingByProcess.set(edge.fromProcessId, []);
      if (!incomingByProcess.has(edge.toProcessId)) incomingByProcess.set(edge.toProcessId, []);

      outgoingByProcess.get(edge.fromProcessId).push(edge.toProcessId);
      incomingByProcess.get(edge.toProcessId).push(edge.fromProcessId);
    });

    const traceabilitySteps = orderedSteps.map((stepNodes) =>
      stepNodes.map((node) => ({
        processId: node.processId,
        processType: node.processType,
        documentNumber: node.documentNumber,
        callOff: node.callOff,
        fruitNames: node.fruitNames,
        returned: node.returned,
        current: node.current,
        nextProcessId: outgoingByProcess.get(node.processId) || [],
        previousProcessId: incomingByProcess.get(node.processId) || [],
      })),
    );

    const newDataSet = [];

    traceabilitySteps.forEach((step, stepIndex) => {
      const nextStep = traceabilitySteps[stepIndex + 1];
      const previousStep = traceabilitySteps[stepIndex - 1];

      step.forEach((processInfo, processIndex) => {
        const nullArray = new Array(stepIndex).fill(null);
        const currentPosition = processIndex;
        const { processType, processId, documentNumber, callOff, fruitNames, returned } = processInfo;

        const displayProcessType =
          processType === 'Despacho' ? t('lotTraceability.dispatch') : processType === 'Recepción'
            ? t('lotTraceability.receipt')
            : processType;

        const primaryReference =
          useCallOffReference && processType === 'Recepción'
            ? callOff || documentNumber || processId
            : documentNumber || processId;

        let label = `${primaryReference} ${displayProcessType}`;
        if (useCallOffReference && processType !== 'Recepción' && callOff) {
          label += ` (${callOff})`;
        }
        if (fruitNames) label += ` (${fruitNames})`;
        if (returned) label += t('lotTraceability.returned');

        let url = `/process/${processId}`;
        let currentBackGroundColor = '#8BC34A';
        let currentBorderColor = '#8BC34A';
        let currentColor = '#FFFFFF';

        if (processType === 'Despacho') {
          currentBackGroundColor = '#2979FF';
          currentBorderColor = '#2979FF';
          url = `/dispatches?dispatchNumber=${documentNumber}`;
        } else if (processType === 'Recepción') {
          currentBackGroundColor = '#FFFFFF';
          currentBorderColor = '#2979FF';
          currentColor = '#2979FF';
          url = `/receipts?lotNumber=${documentNumber}`;
        }

        if (processInfo.current) {
          currentBorderColor = 'red';
        }

        const hiddePointStyle = {
          label: null,
          borderWidth: 1,
          borderColor: '#358A7C',
          showLabel: false,
          fill: false,
          pointRadius: -1,
          pointHoverRadius: -1,
        };

        const displayPointStyle = {
          label,
          currentBorderColor,
          currentBackGroundColor,
          currentColor,
          pointBorderColor: -1,
          pointBackgroundColor: -1,
          showLabel: true,
          fill: false,
        };

        newDataSet.push({
          ...displayPointStyle,
          label,
          showLabel: true,
          data: [...nullArray, currentPosition],
          url,
        });

        if (nextStep && processInfo.nextProcessId) {
          const nextProcessesInfos = nextStep.filter((info) =>
            processInfo.nextProcessId.includes(info.processId),
          );
          const nextPositions = nextProcessesInfos.map((info) =>
            nextStep.findIndex((pinfo) => pinfo.processId === info.processId),
          );
          nextPositions.forEach((position) => {
            newDataSet.push({
              data: [...nullArray, currentPosition, position],
              ...hiddePointStyle,
            });
          });
        }

        if (previousStep && processInfo.previousProcessId) {
          const previousProcessesInfos = previousStep.filter((info) =>
            processInfo.previousProcessId.includes(info.processId),
          );
          const previousPositions = previousProcessesInfos.map((info) =>
            previousStep.findIndex((pinfo) => pinfo.processId === info.processId),
          );
          nullArray.pop();

          previousPositions.forEach((position) => {
            newDataSet.push({
              pointRadius: -1,
              pointHoverRadius: -1,
              data: [...nullArray, position, currentPosition],
              ...hiddePointStyle,
            });
          });
        }
      });
    });

    setDataLabels(new Array(traceabilitySteps.length).fill(''));
    setDataSet(newDataSet);
  };

  const createDataSets = () => {
    const newDataSet = [];
    traceabilityReport.forEach((step, stepIndex) => {
      const nextStep = traceabilityReport[stepIndex + 1];
      const previousStep = traceabilityReport[stepIndex - 1];

      step.forEach((processInfo, processIndex) => {
        const nullArray = new Array(stepIndex).fill(null);
        const currentPosition = processIndex;
        const { processType, processId, documentNumber, callOff, fruitNames, returned } = processInfo;

        const displayProcessType =
          // eslint-disable-next-line no-nested-ternary
          processType === 'Despacho'
            ? t('lotTraceability.dispatch')
            : processType === 'Recepción'
              ? t('lotTraceability.receipt')
              : processType;

        const primaryReference =
          useCallOffReference && processType === 'Recepción'
            ? callOff || documentNumber || processId
            : documentNumber || processId;

        let label = `${primaryReference} ${displayProcessType}`;
        if (useCallOffReference && processType !== 'Recepción' && callOff) {
          label += ` (${callOff})`;
        }
        if (fruitNames) label += ` (${fruitNames})`;
        if (returned) label += t('lotTraceability.returned');

        let url = `/process/${processId}`;
        let nextPositions = [];
        let previousPositions = [];
        let currentBackGroundColor = '#8BC34A';
        let currentBorderColor = '#8BC34A';
        let currentColor = '#FFFFFF';

        if (processType === 'Despacho') {
          currentBackGroundColor = '#2979FF';
          currentBorderColor = '#2979FF';
          url = `/dispatches?dispatchNumber=${documentNumber}`;
        } else if (processType === 'Recepción') {
          currentBackGroundColor = '#FFFFFF';
          currentBorderColor = '#2979FF';
          currentColor = '#2979FF';
          url = `/receipts?lotNumber=${documentNumber}`;
        }

        if (processInfo.current) {
          currentBorderColor = 'red';
        }

        const hiddePointStyle = {
          label: null,
          borderWidth: 1,
          borderColor: '#358A7C',
          showLabel: false,
          fill: false,
          pointRadius: -1,
          pointHoverRadius: -1,
        };

        const displayPointStyle = {
          label,
          currentBorderColor,
          currentBackGroundColor,
          currentColor,
          pointBorderColor: -1,
          pointBackgroundColor: -1,
          showLabel: true,
          fill: false,
        };

        // Primero se diagraman los puntos con sus colores pero sin recorridos
        newDataSet.push({
          ...displayPointStyle,
          label,
          showLabel: true,
          data: [...nullArray, currentPosition],
          url,
        });

        // Luego se diagraman las lineas con tramos de puntos y dichos puntos estan ocultos, solo se ven las lineas
        if (nextStep && processInfo.nextProcessId) {
          const nextProcessesInfos = nextStep.filter((info) =>
            processInfo.nextProcessId.includes(info.processId),
          );
          nextPositions = nextProcessesInfos.map((info) =>
            nextStep.findIndex((pinfo) => pinfo.processId === info.processId),
          );
          nextPositions.forEach((position) => {
            newDataSet.push({
              data: [...nullArray, currentPosition, position],
              ...hiddePointStyle,
            });
          });
        }

        if (previousStep && processInfo.previousProcessId) {
          const previousProcessesInfos = previousStep.filter((info) =>
            processInfo.previousProcessId.includes(info.processId),
          );
          previousPositions = previousProcessesInfos.map((info) =>
            previousStep.findIndex((pinfo) => pinfo.processId === info.processId),
          );
          nullArray.pop();

          previousPositions.forEach((position) => {
            newDataSet.push({
              pointRadius: -1,
              pointHoverRadius: -1,
              data: [...nullArray, position, currentPosition],
              ...hiddePointStyle,
            });
          });
        }
      });
    });

    setDataLabels(new Array(traceabilityReport.length).fill(''));
    setDataSet(newDataSet);
  };

  useEffect(() => {
    if (controls?.lotNumber) {
      setDataSet([]);
      setDataLabels([]);
      return;
    }

    if (traceabilityReport.length > 0 && !loading) {
      createDataSets();
    } else {
      setDataSet([]);
    }
  }, [traceabilityReport, traceabilityGraph, controls, loading]);

  const pointOnClick = (data) => {
    history.push(data.url);
  };

  return (
    <Components
      loading={loading}
      dataSet={dataSet}
      dataLabels={dataLabels}
      controls={controls}
      traceabilityGraph={traceabilityGraph}
      classes={classes}
      pointOnClick={pointOnClick}
      queueProcess={queueProcess}
      refreshData={refreshData}
      customChart={controls?.lotNumber ? (
        <ProcessTraceabilityChart
          traceabilityGraph={traceabilityGraph}
          pointOnClick={pointOnClick}
        />
      ) : null}
    />
  );
}

export default connectScreen(
  'LotTraceability',
  LotTraceability,
  saga,
  { ...lotTraceabilityActions, ...appActions },
  selectState(
    'lotTraceability',
    'app.objects',
    'app.queueProcess',
    'app.queueData',
    'app.loadFromNoti',
    'app.socketId',
  ),
  styles,
);
